import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  ViewChild,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { NGXLogger } from 'ngx-logger';
import { Subscription, fromEvent } from 'rxjs';

/** Menu Base class. */
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class MenuBaseComponent {
  /** Mat Menu Trigger Reference. */
  @ViewChild(MatMenuTrigger, { static: true })
  matMenuTrigger: MatMenuTrigger;
  /** Menu Position styling. */
  menuRootStyle: {
    /** Left position in px. */
    'left.px': number;
    /** Top position in px. */
    'top.px': number;
  };

  private listenerSubscription: Subscription;
  private menuOpen = false;

  /** Handler for the menu close event. */
  onMenuClosed(): void {
    this.listenerSubscription.unsubscribe();
  }

  /**
   * Opens the menu.
   *
   * @param mouseEvent Mouse event.
   */
  open(mouseEvent: MouseEvent): void {
    mouseEvent.stopPropagation();
    // Record the mouse position in our object.
    this.menuRootStyle = {
      'left.px': mouseEvent.clientX,
      'top.px': mouseEvent.clientY,
    };

    // Open the menu
    this.menuOpen = true;
    this.matMenuTrigger.openMenu();
    this.listenerSubscription = fromEvent(window, 'contextmenu').subscribe(
      (event) => {
        if (this.menuOpen) {
          event.preventDefault();
          this.matMenuTrigger.closeMenu();
          this.menuOpen = false;
          const eventTarget = event.target;
          if (
            !(eventTarget instanceof Element) ||
            this.isElementDisabled(eventTarget)
          ) {
            // Prevent the click event from being dispatched below if the target is disabled.
            return;
          }

          eventTarget.dispatchEvent(new MouseEvent('click', event));
        }
      }
    );
  }

  /**
   * Determines whether the provided element (or one of its actionable ancestors) is disabled.
   *
   * @param target Event target element.
   * @returns True when the click target resolves to a disabled control.
   */
  private isElementDisabled(target: Element): boolean {
    const clickableElement = target.closest(
      'button, [mat-menu-item], [role="menuitem"], [role="button"]'
    );
    if (!(clickableElement instanceof HTMLElement)) {
      return false;
    }

    if ('disabled' in clickableElement && clickableElement.disabled) {
      return true;
    }

    const ariaDisabled = clickableElement.getAttribute('aria-disabled');
    if (ariaDisabled === 'true') {
      return true;
    }

    // Angular Material marks disabled items with this class.
    return clickableElement.classList.contains('mat-mdc-menu-item-disabled');
  }
}

/** Describes a copyable menu. */
export interface Copyable {
  /** The value that will be copied to the clipboard when {@link copyToClipboard} is called. */
  copyValue: WritableSignal<string>;
  /** Whether there is a value to be copied. */
  hasCopyValue: Signal<boolean>;
  /**
   * Copies the {@link copyValue} to the clipboard.
   *
   * @returns True if the copy to the clipboard was successful. Otherwise, false.
   */
  copyToClipboard(): boolean;
}

// biome-ignore lint/suspicious/noExplicitAny: TypeScript mixin constructors require any[]
type MenuBaseConstructor = abstract new (...args: any[]) => MenuBaseComponent;

/** Menu base component that also contains copy functionality. */
export const CopyableMenuBase = withCopyable(MenuBaseComponent);

/** Adds copyable functionality to a menu base component. */
export function withCopyable<TBase extends MenuBaseConstructor>(
  Base: TBase
): abstract new (
  ...args: ConstructorParameters<TBase>
) => InstanceType<TBase> & Copyable {
  abstract class CopyableMixin extends Base implements Copyable {
    private logger = inject(NGXLogger);
    private clipboard = inject(Clipboard);

    /** @inheritdoc */
    copyValue = signal('');
    /** @inheritdoc */
    hasCopyValue = computed(() => !!this.copyValue());

    /** @inheritdoc */
    copyToClipboard(): boolean {
      const value = this.copyValue();
      if (!value) {
        this.logger.debug('No value to copy.');
        return false;
      }

      return this.clipboard.copy(value);
    }

    /** @inheritdoc */
    override onMenuClosed(): void {
      this.copyValue.set('');
      super.onMenuClosed();
    }
  }

  // We need to do this to ensure the return type still contains menu base.
  return CopyableMixin as unknown as abstract new (
    ...args: ConstructorParameters<TBase>
  ) => InstanceType<TBase> & Copyable;
}
